import { useEffect, useRef } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { useQueryClient } from "@tanstack/react-query"
import { signOut } from "firebase/auth"
import { auth } from "@/lib/firebase"
import { useSnackbar } from "@/hooks/useSnackbar"

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour
const CHECK_INTERVAL_MS = 60 * 1000 // check every minute
const ACTIVITY_THROTTLE_MS = 10 * 1000 // record activity at most once per 10s
const ACTIVITY_STORAGE_KEY = "last_activity_timestamp"

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"]

// localStorage can throw in private browsing, sandboxed iframes, or when the
// quota is exhausted. Treat those failures as a no-op so inactivity tracking
// keeps working off the in-memory ref.
const safeReadActivity = () => {
  try {
    const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY)
    const parsed = raw == null ? 0 : Number(raw)
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return 0
  }
}

const safeWriteActivity = (timestamp) => {
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(timestamp))
  } catch {
    /* private mode / quota — fall back to ref-only */
  }
}

export const useInactivityLogout = () => {
  const [user] = useAuthState(auth)
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const lastActivityRef = useRef(Date.now())

  useEffect(() => {
    if (!user) return

    const now = Date.now()
    lastActivityRef.current = now
    safeWriteActivity(now)

    // Leading-edge throttle: mousemove/scroll fire hundreds of times per second
    // and writing to localStorage on every fire blocks the main thread.
    let lastWriteAt = now
    const updateActivity = () => {
      const timestamp = Date.now()
      lastActivityRef.current = timestamp
      if (timestamp - lastWriteAt < ACTIVITY_THROTTLE_MS) return
      lastWriteAt = timestamp
      safeWriteActivity(timestamp)
    }

    let hasSignedOut = false
    let interval

    const checkInactivity = async () => {
      if (hasSignedOut) return
      // Check cross-tab activity so a background tab doesn't sign out
      // a user who is active in another tab
      const lastCrossTabActivity = safeReadActivity()
      const lastActivity = Math.max(lastActivityRef.current, lastCrossTabActivity)
      if (Date.now() - lastActivity < INACTIVITY_TIMEOUT_MS) return

      // Latch immediately and stop the interval so a slow signOut can't let
      // a second tick re-emit the snackbar or re-call signOut.
      hasSignedOut = true
      clearInterval(interval)
      showSnackbar({ message: "Signed out due to inactivity", severity: "info" })
      try {
        await signOut(auth)
        // Wipe cached query data so a different account logging in next can't
        // briefly see this user's tenant-scoped rows.
        queryClient.clear()
      } catch (err) {
        if (import.meta.env.DEV) console.error("Inactivity signOut failed:", err)
      }
    }

    for (const event of ACTIVITY_EVENTS) {
      globalThis.addEventListener(event, updateActivity, { passive: true })
    }

    interval = setInterval(checkInactivity, CHECK_INTERVAL_MS)

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        globalThis.removeEventListener(event, updateActivity)
      }
      clearInterval(interval)
    }
  }, [user, showSnackbar, queryClient])
}
